import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  currentStep: 1,
  totalSteps: 6,
  stepData: {},
  profile: null,
  recommendations: {},
  isLoading: false,
  error: null,
  isCompleted: false,
  hasUnsavedChanges: false,
  lastSavedAt: null,
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    // Step navigation actions
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    nextStep: (state) => {
      if (state.currentStep < state.totalSteps - 1) {
        state.currentStep += 1;
      }
    },
    previousStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },
    setTotalSteps: (state, action) => {
      state.totalSteps = action.payload;
    },

    // Data persistence actions
    updateStepData: (state, action) => {
      const { step, data } = action.payload;
      state.stepData[step] = { ...state.stepData[step], ...data };
      state.hasUnsavedChanges = true;
    },
    setStepData: (state, action) => {
      state.stepData = action.payload;
      state.hasUnsavedChanges = false;
    },
    clearStepData: (state) => {
      state.stepData = {};
      state.hasUnsavedChanges = false;
    },

    // Profile management
    setProfile: (state, action) => {
      state.profile = action.payload;
    },
    updateProfile: (state, action) => {
      state.profile = { ...state.profile, ...action.payload };
      state.hasUnsavedChanges = true;
    },

    // Recommendations
    setRecommendations: (state, action) => {
      state.recommendations = action.payload;
    },
    updateRecommendations: (state, action) => {
      state.recommendations = { ...state.recommendations, ...action.payload };
    },

    // API state management
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },

    // Completion status
    setCompleted: (state, action) => {
      state.isCompleted = action.payload;
      if (action.payload) {
        state.hasUnsavedChanges = false;
      }
    },

    // Auto-save functionality
    markSaved: (state) => {
      state.hasUnsavedChanges = false;
      state.lastSavedAt = new Date().toISOString();
    },
    markUnsaved: (state) => {
      state.hasUnsavedChanges = true;
    },

    // Reset onboarding state
    resetOnboarding: (_state) => {
      return { ...initialState };
    },

    // Async action handlers
    initializeOnboardingStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    initializeOnboardingSuccess: (state, action) => {
      const { profile, currentStep, stepData, recommendations, isCompleted } = action.payload;
      state.profile = profile;
      state.currentStep = currentStep || 0;
      state.stepData = stepData || {};
      state.recommendations = recommendations || {};
      state.isCompleted = isCompleted || false;
      state.isLoading = false;
      state.error = null;
      state.hasUnsavedChanges = false;
    },
    initializeOnboardingFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    saveProgressStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    saveProgressSuccess: (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.hasUnsavedChanges = false;
      state.lastSavedAt = new Date().toISOString();
      if (action.payload) {
        const { current_step, id, is_completed, onboarding_completed } =
          action.payload;
        state.profile = {
          ...state.profile,
          ...(id !== undefined && { id }),
          ...(current_step !== undefined && { current_step }),
          ...(is_completed !== undefined && { is_completed }),
          ...(onboarding_completed !== undefined && { onboarding_completed }),
        };
      }
    },
    saveProgressFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    completeOnboardingStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    completeOnboardingSuccess: (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.isCompleted = true;
      state.hasUnsavedChanges = false;
      state.lastSavedAt = new Date().toISOString();
      if (action.payload) {
        state.profile = { ...state.profile, ...action.payload };
      }
    },
    completeOnboardingFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

export const {
  // Step navigation
  setCurrentStep,
  nextStep,
  previousStep,
  setTotalSteps,
  
  // Data persistence
  updateStepData,
  setStepData,
  clearStepData,
  
  // Profile management
  setProfile,
  updateProfile,
  
  // Recommendations
  setRecommendations,
  updateRecommendations,
  
  // API state
  setLoading,
  setError,
  clearError,
  
  // Completion
  setCompleted,
  
  // Auto-save
  markSaved,
  markUnsaved,
  
  // Reset
  resetOnboarding,
  
  // Async actions
  initializeOnboardingStart,
  initializeOnboardingSuccess,
  initializeOnboardingFailure,
  saveProgressStart,
  saveProgressSuccess,
  saveProgressFailure,
  completeOnboardingStart,
  completeOnboardingSuccess,
  completeOnboardingFailure,
} = onboardingSlice.actions;

// Selectors
export const selectOnboardingState = (state) => state.onboarding;
export const selectCurrentStep = (state) => state.onboarding.currentStep;
export const selectTotalSteps = (state) => state.onboarding.totalSteps;
export const selectStepData = (state) => state.onboarding.stepData;
export const selectStepDataByStep = (step) => (state) => state.onboarding.stepData[step] || {};
export const selectProfile = (state) => state.onboarding.profile;
export const selectRecommendations = (state) => state.onboarding.recommendations;
export const selectIsLoading = (state) => state.onboarding.isLoading;
export const selectError = (state) => state.onboarding.error;
export const selectIsCompleted = (state) => state.onboarding.isCompleted;
export const selectHasUnsavedChanges = (state) => state.onboarding.hasUnsavedChanges;
export const selectLastSavedAt = (state) => state.onboarding.lastSavedAt;
export const selectProgress = (state) => {
  const { currentStep, totalSteps } = state.onboarding;
  return totalSteps > 0 ? Math.round((currentStep / totalSteps) * 100) : 0;
};
export const selectCanGoNext = (state) => {
  const { currentStep, totalSteps } = state.onboarding;
  return currentStep < totalSteps - 1;
};
export const selectCanGoPrevious = (state) => state.onboarding.currentStep > 0;

export default onboardingSlice.reducer;